/**
 * OtpForms — OTP send + verify flows with vault styling.
 */
import { useState } from "react";
import { Mail, KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/* ---- OTP Send ---- */
interface OtpSendProps {
  initialEmail?: string;
  onSendOtp: (email: string) => Promise<void>;
  onBack: () => void;
  loading: boolean;
}

export function OtpSendForm({ initialEmail = "", onSendOtp, onBack, loading }: OtpSendProps) {
  const [email, setEmail] = useState(initialEmail);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSendOtp(email);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="otp-email" className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Email</Label>
        <Input
          id="otp-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-10"
        />
      </div>
      <Button type="submit" disabled={loading} className="w-full gap-2 h-10">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
        Send 6-digit code
      </Button>
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-muted-foreground hover:text-foreground font-mono"
      >
        ← Back to password
      </button>
    </form>
  );
}

/* ---- OTP Verify ---- */
interface OtpVerifyProps {
  email: string;
  onVerify: (token: string) => Promise<void>;
  loading: boolean;
}

export function OtpVerifyForm({ email, onVerify, loading }: OtpVerifyProps) {
  const [code, setCode] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onVerify(code);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Enter the 6-digit code sent to <strong>{email}</strong>
      </p>
      <div className="space-y-1.5">
        <Label htmlFor="otp-code" className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Code</Label>
        <Input
          id="otp-code"
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          autoFocus
          className="h-12 text-center text-lg tracking-[0.5em] font-mono"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        />
      </div>
      <Button type="submit" disabled={loading || code.length < 6} className="w-full gap-2 h-10">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
        Verify
      </Button>
    </form>
  );
}
